import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Button, DatePicker, Empty, Input, List, Popconfirm, Select, Space, Spin, Tag, Typography, Upload, message } from "antd";
import {
  AlertOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  SendOutlined,
  TagsOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import RichTextEditor from "components/common/RichTextEditor";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useProcessedDescription from "hooks/useProcessedDescription";
import * as pmService from "services/pmService";
import {
  clearRichTextImageMaps,
  createRichTextImageMaps,
  normalizeRichTextImagesForSave,
  processRichTextImagesForEdit,
  uploadRichTextImage,
} from "utils/richTextImages";
import { OLIVEEB_COLORS } from "workspaces/colors";
import paths from "nav/paths";

const { Text } = Typography;

const GUIDE_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"];
const INCIDENT_STATUSES = ["OPEN", "INVESTIGATING", "MITIGATED", "RESOLVED", "CLOSED"];
const INCIDENT_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const TICKET_STATUSES = ["OPEN", "IN_PROGRESS", "BLOCKED", "RESOLVED", "CLOSED", "CANCELLED"];
const TICKET_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];
const TICKET_TYPES = ["SUPPORT", "INCIDENT", "CHANGE", "TASK"];
const RICH_TEXT_FIELDS_BY_RESOURCE = {
  guides: ["description", "symptoms", "steps", "verificationSteps", "rollbackSteps"],
  incidents: ["description", "impact", "rootCause", "resolution"],
  tickets: ["description"],
};

const labelize = (value = "") =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const optionList = (items) => items.map((item) => ({ value: item, label: labelize(item) }));
const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "Not set");
const recordNumber = (record = {}) => record.number || record.key || "Pending";
const pageItems = (page) => (Array.isArray(page?.content) ? page.content : []);

const isRichTextEmpty = (value = "") =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length === 0;

const textWrapStyle = {
  display: "block",
  maxWidth: "100%",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
};

const statusColor = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (["ACTIVE", "RESOLVED", "CLOSED"].includes(normalized)) return "green";
  if (["CRITICAL", "URGENT", "BLOCKED"].includes(normalized)) return "red";
  if (["HIGH", "INVESTIGATING", "IN_PROGRESS"].includes(normalized)) return "orange";
  if (["ARCHIVED", "CANCELLED"].includes(normalized)) return "default";
  return "blue";
};

const statusTag = (value) =>
  value ? <Tag color={statusColor(value)}>{labelize(value)}</Tag> : <Text type="secondary">Not set</Text>;

const detailText = (value) =>
  value ? <Text style={textWrapStyle}>{value}</Text> : <Text type="secondary">Not set</Text>;

const richTextViewStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 4,
  padding: 12,
  background: "#fafafa",
  color: "#374151",
  fontSize: 13,
  lineHeight: 1.6,
  width: "100%",
};

const ProcessedRichTextView = ({ value }) => {
  const processedValue = useProcessedDescription(value, []);

  return isRichTextEmpty(value) ? (
    <Text type="secondary">Not set</Text>
  ) : (
    <div
      className="operation-rich-text-view"
      style={richTextViewStyle}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedValue || value) }}
    />
  );
};

const detailHtmlText = (value) => <ProcessedRichTextView value={value} />;

const MetaRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 14 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        flex: "0 0 180px",
        fontSize: 13,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>{value}</div>
  </div>
);

const EditableField = ({ isEditing, saving, display, editor, onEdit, onSave, onCancel }) => {
  if (isEditing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {editor}
        <CheckOutlined
          onClick={saving ? undefined : onSave}
          style={{ cursor: saving ? "not-allowed" : "pointer", color: saving ? "#9ca3af" : "#22c55e" }}
        />
        <CloseOutlined
          onClick={saving ? undefined : onCancel}
          style={{ cursor: saving ? "not-allowed" : "pointer", color: "#ef4444" }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} onDoubleClick={onEdit}>
      {display}
    </div>
  );
};

const commentAuthorName = (comment = {}) => {
  if (comment.author) {
    return `${comment.author.firstName || ""} ${comment.author.lastName || ""}`.trim() || "User";
  }
  return comment.createdByName || comment.authorName || comment.createdBy || "User";
};

const userDisplayName = (user = {}) =>
  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
  user.email ||
  user.username ||
  String(user.id || user.userId || user.uid || "");

const initialsFor = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0]?.[0] || ""}${parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : ""}`.toUpperCase();
};

const OperationCommentsSection = ({
  comments,
  loading,
  submitting,
  savingCommentId,
  deletingCommentId,
  newComment,
  setNewComment,
  editingCommentId,
  editingCommentContent,
  setEditingCommentContent,
  onAdd,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}) => (
  <div
    style={{
      border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
      borderRadius: 8,
      background: OLIVEEB_COLORS.white,
      padding: 20,
      boxShadow: `0 10px 28px ${OLIVEEB_COLORS.shadow}`,
      marginTop: 18,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: OLIVEEB_COLORS.oliveebDarkGreen, fontWeight: 700 }}>
        <CommentOutlined />
        <span>Comments ({comments.length})</span>
      </div>
    </div>

    <form onSubmit={onAdd}>
      <RichTextEditor
        placeholder="Add a comment..."
        value={newComment}
        onChange={setNewComment}
        height="150px"
        readOnly={submitting}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Button
          type="primary"
          htmlType="submit"
          icon={<SendOutlined />}
          loading={submitting}
          disabled={isRichTextEmpty(newComment)}
        >
          Comment
        </Button>
      </div>
    </form>

    <div style={{ marginTop: 18 }}>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
          <Spin />
        </div>
      ) : comments.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={comments}
          renderItem={(comment) => {
            const authorName = commentAuthorName(comment);
            const createdAt = comment.createdAt || comment.createdOn;
            const isEditing = editingCommentId === comment.id;

            return (
              <List.Item key={comment.id} id={`comment-${comment.id}`} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <Avatar>{initialsFor(authorName)}</Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <Text strong>{authorName}</Text>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          {createdAt ? formatDate(createdAt) : ""}
                        </Text>
                      </div>
                      {!isEditing ? (
                        <Space size={8}>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => onStartEdit(comment)}
                          />
                          <Popconfirm
                            title="Delete comment?"
                            okText="Delete"
                            okButtonProps={{ danger: true, loading: deletingCommentId === comment.id }}
                            onConfirm={() => onDelete(comment.id)}
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      ) : null}
                    </div>

                    {isEditing ? (
                      <div style={{ marginTop: 8 }}>
                        <RichTextEditor
                          value={editingCommentContent}
                          onChange={setEditingCommentContent}
                          height="150px"
                          placeholder="Update comment..."
                          readOnly={savingCommentId === comment.id}
                        />
                        <Space style={{ marginTop: 8 }}>
                          <Button onClick={onCancelEdit}>Cancel</Button>
                          <Button
                            type="primary"
                            loading={savingCommentId === comment.id}
                            disabled={isRichTextEmpty(editingCommentContent)}
                            onClick={() => onSaveEdit(comment.id)}
                          >
                            Save
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <div
                        className="operation-comment-body"
                        style={{
                          marginTop: 8,
                          padding: 12,
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          background: "#f9fafb",
                          color: "#374151",
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(comment.content || ""),
                        }}
                      />
                    )}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  </div>
);

const OperationAttachmentsSection = ({
  attachments,
  loading,
  uploading,
  deletingAttachmentId,
  onUpload,
  onDelete,
}) => (
  <div
    style={{
      border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
      borderRadius: 8,
      background: OLIVEEB_COLORS.white,
      padding: 20,
      boxShadow: `0 10px 28px ${OLIVEEB_COLORS.shadow}`,
      marginTop: 18,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: OLIVEEB_COLORS.oliveebDarkGreen, fontWeight: 700 }}>
        <PaperClipOutlined />
        <span>Attachments ({attachments.length})</span>
      </div>
      <Upload
        multiple
        showUploadList={false}
        disabled={uploading}
        customRequest={({ file, onError, onSuccess }) => {
          onUpload(file)
            .then(() => onSuccess?.("ok"))
            .catch((error) => onError?.(error));
        }}
      >
        <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
          Attach file
        </Button>
      </Upload>
    </div>

    {loading ? (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
        <Spin />
      </div>
    ) : attachments.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments yet" />
    ) : (
      <List
        dataSource={attachments}
        renderItem={(attachment) => {
          const name = attachment.label || attachment.fileName || attachment.fileId || "Attachment";
          return (
            <List.Item
              key={attachment.id || attachment.fileId}
              actions={[
                <Popconfirm
                  key="delete"
                  title="Delete attachment?"
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: deletingAttachmentId === attachment.id }}
                  onConfirm={() => onDelete(attachment.id)}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 18 }} />}
                title={
                  attachment.fileUrl ? (
                    <a href={attachment.fileUrl} target="_blank" rel="noreferrer">
                      {name}
                    </a>
                  ) : (
                    <span>{name}</span>
                  )
                }
                description={attachment.createdAt ? `Attached ${formatDate(attachment.createdAt)}` : attachment.fileName}
              />
            </List.Item>
          );
        }}
      />
    )}
  </div>
);

const resourceConfig = {
  guides: {
    title: "Troubleshooting Guide",
    pluralTitle: "Troubleshooting Guides",
    route: paths.troubleshootingGuides,
    idParam: "guideId",
    icon: <BookOutlined />,
    get: pmService.getTroubleshootingGuide,
    update: pmService.updateTroubleshootingGuide,
    statuses: GUIDE_STATUSES,
    commentResourcePath: "troubleshooting-guides",
    attachmentResourcePath: "troubleshooting-guides",
  },
  incidents: {
    title: "Incident",
    pluralTitle: "Incidents",
    route: paths.incidents,
    idParam: "incidentId",
    icon: <AlertOutlined />,
    get: pmService.getIncident,
    update: pmService.updateIncident,
    statuses: INCIDENT_STATUSES,
    attachmentResourcePath: "incidents",
  },
  tickets: {
    title: "Ticket",
    pluralTitle: "Tickets",
    route: paths.tickets,
    idParam: "ticketId",
    icon: <TagsOutlined />,
    get: pmService.getTicket,
    update: pmService.updateTicket,
    statuses: TICKET_STATUSES,
    commentResourcePath: "tickets",
    attachmentResourcePath: "tickets",
  },
};

const removeUndefined = (payload) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));

const normalizeEditValue = (value) => (value?.toISOString ? value.toISOString() : value);

const buildUpdatePayload = (resource, record = {}, overrides = {}) => {
  const next = { ...record, ...overrides };
  const common = {
    title: next.title || "",
    description: next.description || "",
    status: next.status,
  };

  if (resource === "guides") {
    return removeUndefined({
      ...common,
      systemArea: next.systemArea,
      symptoms: next.symptoms,
      steps: next.steps,
      verificationSteps: next.verificationSteps,
      rollbackSteps: next.rollbackSteps,
      escalationContacts: next.escalationContacts,
    });
  }

  if (resource === "incidents") {
    return removeUndefined({
      ...common,
      projectId: next.projectId,
      affectedSystem: next.affectedSystem,
      impact: next.impact,
      rootCause: next.rootCause,
      resolution: next.resolution,
      reportedBy: next.reportedBy,
      assignedTo: next.assignedTo,
      severity: next.severity,
      troubleshootingGuideId: next.troubleshootingGuideId,
      detectedAt: next.detectedAt,
      mitigatedAt: next.mitigatedAt,
      resolvedAt: next.resolvedAt,
    });
  }

  return removeUndefined({
    ...common,
    projectId: next.projectId,
    type: next.type,
    priority: next.priority,
    incidentId: next.incidentId,
    assigneeId: next.assigneeId,
    requesterName: next.requesterName,
    dueAt: next.dueAt,
    closedAt: next.closedAt,
  });
};

const OperationDetailsPage = ({ resource = "guides" }) => {
  const config = resourceConfig[resource] || resourceConfig.guides;
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const recordId = params[config.idParam];

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [projects, setProjects] = useState([]);
  const [guides, setGuides] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [savingCommentId, setSavingCommentId] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const richTextImageMapsRef = useRef(createRichTextImageMaps());
  const richTextUploadsInFlightRef = useRef(0);
  const richTextFields = RICH_TEXT_FIELDS_BY_RESOURCE[resource] || ["description"];
  const supportsComments = Boolean(config.commentResourcePath);
  const supportsAttachments = Boolean(config.attachmentResourcePath);
  const currentUserName = userDisplayName(user);

  const loadRecord = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    try {
      setRecord(await config.get(recordId));
    } catch (error) {
      message.error(error?.message || `Failed to load ${config.title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [config, recordId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const loadComments = useCallback(async () => {
    if (!supportsComments || !recordId) {
      setComments([]);
      return;
    }
    setCommentsLoading(true);
    try {
      const data = await pmService.listOperationComments(config.commentResourcePath, recordId);
      setComments(data);
    } catch (error) {
      console.error("[OperationDetailsPage] failed to load comments", error);
      message.error(error?.message || "Failed to load comments.");
    } finally {
      setCommentsLoading(false);
    }
  }, [config.commentResourcePath, recordId, supportsComments]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const loadAttachments = useCallback(async () => {
    if (!supportsAttachments || !recordId) {
      setAttachments([]);
      return;
    }
    setAttachmentsLoading(true);
    try {
      const data = await pmService.listOperationAttachments(config.attachmentResourcePath, recordId);
      setAttachments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[OperationDetailsPage] failed to load attachments", error);
      message.error(error?.message || "Failed to load attachments.");
    } finally {
      setAttachmentsLoading(false);
    }
  }, [config.attachmentResourcePath, recordId, supportsAttachments]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleAddComment = async (event) => {
    event.preventDefault();
    if (!supportsComments || isRichTextEmpty(newComment)) return;

    setCommentSubmitting(true);
    try {
      const created = await pmService.addOperationComment(
        config.commentResourcePath,
        recordId,
        { content: newComment.trim() },
        actorId,
        currentUserName,
      );
      setComments((current) => [created, ...current]);
      setNewComment("");
      message.success("Comment added.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to add comment.");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const startCommentEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content || "");
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentContent("");
  };

  const handleUpdateComment = async (commentId) => {
    if (!supportsComments || isRichTextEmpty(editingCommentContent)) return;

    setSavingCommentId(commentId);
    try {
      const updated = await pmService.updateOperationComment(
        config.commentResourcePath,
        recordId,
        commentId,
        { content: editingCommentContent.trim() },
        actorId,
        currentUserName,
      );
      setComments((current) =>
        current.map((comment) => (comment.id === commentId ? updated : comment)),
      );
      cancelCommentEdit();
      message.success("Comment updated.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to update comment.");
    } finally {
      setSavingCommentId(null);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!supportsComments) return;

    setDeletingCommentId(commentId);
    try {
      await pmService.deleteOperationComment(config.commentResourcePath, recordId, commentId);
      setComments((current) => current.filter((comment) => comment.id !== commentId));
      message.success("Comment deleted.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to delete comment.");
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!supportsAttachments || !file) return;
    if (!actorId) {
      message.error("User ID is required to attach files.");
      return;
    }

    setAttachmentUploading(true);
    try {
      const uploaded = await uploadFile(actorId, file, {
        purpose: "USER_PROFILE",
        fileType: file.type?.startsWith("image/") ? "PHOTO" : "DOCUMENT",
      });
      const created = await pmService.addOperationAttachments(
        config.attachmentResourcePath,
        recordId,
        [{
          fileId: uploaded.fileId,
          fileName: file.name,
          label: file.name,
        }],
        actorId,
      );
      setAttachments((current) => [...created, ...current]);
      message.success("File attached.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to attach file.");
      throw error;
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!supportsAttachments || !attachmentId) return;

    setDeletingAttachmentId(attachmentId);
    try {
      await pmService.deleteOperationAttachment(
        config.attachmentResourcePath,
        recordId,
        attachmentId,
        actorId,
      );
      setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
      message.success("Attachment deleted.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to delete attachment.");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  useEffect(() => {
    const loadReferences = async () => {
      try {
        const [projectsPage, guidesPage] = await Promise.all([
          resource !== "guides"
            ? pmService.listProjects({ page: 0, size: 100, sort: "name,asc" })
            : Promise.resolve({ content: [] }),
          resource === "incidents"
            ? pmService.listTroubleshootingGuides({ page: 0, size: 100, sort: "title,asc" })
            : Promise.resolve({ content: [] }),
        ]);
        setProjects(pageItems(projectsPage));
        setGuides(pageItems(guidesPage));
      } catch {
        setProjects([]);
        setGuides([]);
      }
    };

    loadReferences();
  }, [resource]);

  const projectName = (projectId) => {
    if (!projectId) return "";
    const project = projects.find((item) => String(item.id) === String(projectId));
    return project?.name || project?.code || projectId;
  };

  const guideTitle = (guideId) => {
    if (!guideId) return "";
    const guide = guides.find((item) => String(item.id) === String(guideId));
    const number = recordNumber(guide);
    return guide?.title ? `${number && number !== "Pending" ? `${number} - ` : ""}${guide.title}` : guideId;
  };

  const startEdit = async (field, value, { rich = false } = {}) => {
    setEditingField(field);
    setEditingValue(value ?? "");
    if (!rich) return;

    clearRichTextImageMaps(richTextImageMapsRef.current);
    try {
      const processed = await processRichTextImagesForEdit(
        value || "",
        richTextImageMapsRef.current,
      );
      setEditingValue(processed || value || "");
    } catch (error) {
      console.error("[OperationDetailsPage] failed to process editor images", error);
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditingValue(null);
  };

  const saveEdit = async () => {
    if (!record?.id || !editingField) return;
    if (richTextFields.includes(editingField) && richTextUploadsInFlightRef.current > 0) {
      message.warning("Please wait for image uploads to finish.");
      return;
    }

    setSavingField(editingField);
    try {
      const nextValue = richTextFields.includes(editingField)
        ? normalizeRichTextImagesForSave(editingValue || "", richTextImageMapsRef.current)
        : normalizeEditValue(editingValue);
      const overrides = { [editingField]: nextValue };
      const payload = buildUpdatePayload(resource, record, overrides);
      if (!String(payload.title || "").trim()) {
        message.error("Title must not be blank");
        return;
      }
      const updated = await config.update(record.id, payload, actorId);
      setRecord(updated || { ...record, ...overrides });
      message.success(`${config.title} updated`);
      cancelEdit();
    } catch (error) {
      message.error(error?.message || `Failed to update ${config.title.toLowerCase()}`);
    } finally {
      setSavingField(null);
    }
  };

  const sectionTitle = (title) => (
    <div style={{ color: "#6b7280", fontSize: 13, fontWeight: 600, margin: "20px 0 12px" }}>{title}</div>
  );

  const handleRichTextImageUpload = useCallback(
    async (file, helpers) => {
      richTextUploadsInFlightRef.current += 1;
      try {
        await uploadRichTextImage({
          file,
          helpers,
          uploadFile,
          userId: actorId,
          maps: richTextImageMapsRef.current,
          onHtmlChange: setEditingValue,
        });
      } catch (error) {
        message.error(error?.message || "Failed to upload image.");
      } finally {
        richTextUploadsInFlightRef.current = Math.max(
          0,
          richTextUploadsInFlightRef.current - 1,
        );
      }
    },
    [actorId, uploadFile],
  );

  const editableText = (field, value, { rich = false, multiline = false, height = "220px" } = {}) => (
    <EditableField
      isEditing={editingField === field}
      saving={savingField === field}
      display={rich ? detailHtmlText(value) : detailText(value)}
      editor={rich ? (
        <div style={{ minWidth: 320, width: "100%" }}>
          <RichTextEditor
            value={editingValue || ""}
            onChange={setEditingValue}
          placeholder="Enter details..."
          height={height}
          className="operation-rich-editor"
          onImageUpload={handleRichTextImageUpload}
          onImagePaste={handleRichTextImageUpload}
        />
      </div>
      ) : (
        <Input.TextArea
          autoSize={{ minRows: multiline ? 3 : 1, maxRows: 8 }}
          value={editingValue}
          onChange={(event) => setEditingValue(event.target.value)}
          style={{ minWidth: 280 }}
        />
      )}
      onEdit={() => startEdit(field, value, { rich })}
      onCancel={cancelEdit}
      onSave={saveEdit}
    />
  );

  const editableSelect = (field, value, options) => (
    <EditableField
      isEditing={editingField === field}
      saving={savingField === field}
      display={statusTag(value)}
      editor={<Select value={editingValue} onChange={setEditingValue} options={optionList(options)} style={{ minWidth: 180 }} />}
      onEdit={() => startEdit(field, value)}
      onCancel={cancelEdit}
      onSave={saveEdit}
    />
  );

  const editableDate = (field, value) => (
    <EditableField
      isEditing={editingField === field}
      saving={savingField === field}
      display={<Text>{formatDate(value)}</Text>}
      editor={
        <DatePicker
          showTime
          value={editingValue}
          onChange={setEditingValue}
          style={{ minWidth: 220 }}
        />
      }
      onEdit={() => startEdit(field, value ? dayjs(value) : null)}
      onCancel={cancelEdit}
      onSave={saveEdit}
    />
  );

  const editableReferenceText = (field, value, displayValue) => (
    <EditableField
      isEditing={editingField === field}
      saving={savingField === field}
      display={detailText(displayValue || value)}
      editor={
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={editingValue}
          onChange={(event) => setEditingValue(event.target.value)}
          style={{ minWidth: 280 }}
        />
      }
      onEdit={() => startEdit(field, value)}
      onCancel={cancelEdit}
      onSave={saveEdit}
    />
  );

  const metaRows = (() => {
    if (!record) return null;
    if (resource === "guides") {
      return (
        <>
          {sectionTitle("Guide Details")}
          <MetaRow icon={<FileTextOutlined />} label="System Area" value={editableText("systemArea", record.systemArea)} />
          <MetaRow icon={<FileTextOutlined />} label="Symptoms" value={editableText("symptoms", record.symptoms, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Steps" value={editableText("steps", record.steps, { rich: true, height: "280px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Verification" value={editableText("verificationSteps", record.verificationSteps, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Rollback" value={editableText("rollbackSteps", record.rollbackSteps, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Escalation Contacts" value={editableText("escalationContacts", record.escalationContacts)} />
        </>
      );
    }
    if (resource === "incidents") {
      return (
        <>
          {sectionTitle("Incident Details")}
          <MetaRow icon={<AlertOutlined />} label="Severity" value={editableSelect("severity", record.severity, INCIDENT_SEVERITIES)} />
          <MetaRow icon={<FileTextOutlined />} label="Affected System" value={editableText("affectedSystem", record.affectedSystem)} />
          <MetaRow icon={<FileTextOutlined />} label="Project" value={editableReferenceText("projectId", record.projectId, projectName(record.projectId))} />
          <MetaRow icon={<FileTextOutlined />} label="Impact" value={editableText("impact", record.impact, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Root Cause" value={editableText("rootCause", record.rootCause, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Resolution" value={editableText("resolution", record.resolution, { rich: true, height: "220px" })} />
          <MetaRow icon={<FileTextOutlined />} label="Reported By" value={editableText("reportedBy", record.reportedBy)} />
          <MetaRow icon={<FileTextOutlined />} label="Assigned To" value={editableText("assignedTo", record.assignedTo)} />
          <MetaRow
            icon={<FileTextOutlined />}
            label="Troubleshooting Guide"
            value={editableReferenceText(
              "troubleshootingGuideId",
              record.troubleshootingGuideId,
              guideTitle(record.troubleshootingGuideId),
            )}
          />
          <MetaRow icon={<CalendarOutlined />} label="Detected" value={editableDate("detectedAt", record.detectedAt)} />
          <MetaRow icon={<CalendarOutlined />} label="Mitigated" value={editableDate("mitigatedAt", record.mitigatedAt)} />
          <MetaRow icon={<CalendarOutlined />} label="Resolved" value={editableDate("resolvedAt", record.resolvedAt)} />
        </>
      );
    }
    return (
      <>
        {sectionTitle("Ticket Details")}
        <MetaRow icon={<TagsOutlined />} label="Type" value={editableSelect("type", record.type, TICKET_TYPES)} />
        <MetaRow icon={<TagsOutlined />} label="Priority" value={editableSelect("priority", record.priority, TICKET_PRIORITIES)} />
        <MetaRow icon={<FileTextOutlined />} label="Project" value={editableReferenceText("projectId", record.projectId, projectName(record.projectId))} />
        <MetaRow icon={<FileTextOutlined />} label="Requester Email" value={editableText("requesterName", record.requesterName)} />
        <MetaRow icon={<FileTextOutlined />} label="Assignee" value={editableText("assigneeId", record.assigneeId)} />
        <MetaRow icon={<FileTextOutlined />} label="Linked Incident" value={editableText("incidentId", record.incidentId)} />
        <MetaRow icon={<CalendarOutlined />} label="Due" value={editableDate("dueAt", record.dueAt)} />
        <MetaRow icon={<CalendarOutlined />} label="Closed" value={editableDate("closedAt", record.closedAt)} />
      </>
    );
  })();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 420 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="project-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(config.route)}>
          Back to {config.pluralTitle}
        </Button>
      </div>
    );
  }

  return (
    <div className="project-page">
      <style>
        {`
          .operation-rich-text-view p {
            margin: 0 0 8px;
          }
          .operation-rich-text-view p:last-child {
            margin-bottom: 0;
          }
          .operation-rich-text-view ol,
          .operation-rich-text-view ul {
            margin: 0 0 8px 22px;
            padding-left: 18px;
          }
          .operation-rich-text-view li {
            margin-bottom: 4px;
          }
          .operation-rich-text-view blockquote {
            margin: 8px 0;
            padding-left: 12px;
            border-left: 3px solid #d1d5db;
            color: #4b5563;
          }
          .operation-rich-text-view pre {
            background: #111827;
            color: #f9fafb;
            padding: 10px 12px;
            border-radius: 4px;
            white-space: pre-wrap;
          }
          .operation-rich-editor {
            width: 100%;
            margin-bottom: 0;
          }
          .operation-rich-editor .ql-container {
            min-height: 180px;
          }
          .operation-comment-body p {
            margin: 0 0 8px;
          }
          .operation-comment-body p:last-child {
            margin-bottom: 0;
          }
          .operation-comment-body ul,
          .operation-comment-body ol {
            margin: 0 0 8px 22px;
            padding-left: 18px;
          }
        `}
      </style>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={config.icon}
          title={record.title || config.title}
          subtitle={`${recordNumber(record)} · Double-click fields to edit inline`}
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(config.route)}>
            Back
          </Button>
          <Button icon={<EditOutlined />} onClick={() => navigate(`${config.route}/${record.id}/edit`)}>
            Full edit
          </Button>
        </Space>
      </div>

      <div
        style={{
          border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
          borderRadius: 8,
          background: OLIVEEB_COLORS.white,
          padding: 20,
          boxShadow: `0 10px 28px ${OLIVEEB_COLORS.shadow}`,
        }}
      >
        {sectionTitle("Basic Details")}
        <MetaRow icon={<FileTextOutlined />} label="Number" value={<Text>{recordNumber(record)}</Text>} />
        <MetaRow
          icon={<FileTextOutlined />}
          label="Title"
          value={
            <EditableField
              isEditing={editingField === "title"}
              saving={savingField === "title"}
              display={<Text strong style={textWrapStyle}>{record.title}</Text>}
              editor={<Input value={editingValue} onChange={(event) => setEditingValue(event.target.value)} style={{ minWidth: 320 }} />}
              onEdit={() => startEdit("title", record.title)}
              onCancel={cancelEdit}
              onSave={saveEdit}
            />
          }
        />
        <MetaRow icon={<TagsOutlined />} label="Status" value={editableSelect("status", record.status, config.statuses)} />
        {resource === "incidents" ? (
          <MetaRow icon={<AlertOutlined />} label="Severity" value={editableSelect("severity", record.severity, INCIDENT_SEVERITIES)} />
        ) : null}
        {resource === "tickets" ? (
          <MetaRow icon={<TagsOutlined />} label="Priority" value={editableSelect("priority", record.priority, TICKET_PRIORITIES)} />
        ) : null}

        {sectionTitle("Summary")}
        <MetaRow icon={<FileTextOutlined />} label="Description" value={editableText("description", record.description, { rich: true, height: "220px" })} />
        <MetaRow icon={<CalendarOutlined />} label="Created" value={<Text>{formatDate(record.createdAt)}</Text>} />
        <MetaRow icon={<CalendarOutlined />} label="Updated" value={<Text>{formatDate(record.updatedAt)}</Text>} />

        {metaRows}
      </div>

      {supportsAttachments ? (
        <OperationAttachmentsSection
          attachments={attachments}
          loading={attachmentsLoading}
          uploading={attachmentUploading}
          deletingAttachmentId={deletingAttachmentId}
          onUpload={handleUploadAttachment}
          onDelete={handleDeleteAttachment}
        />
      ) : null}

      {supportsComments ? (
        <OperationCommentsSection
          comments={comments}
          loading={commentsLoading}
          submitting={commentSubmitting}
          savingCommentId={savingCommentId}
          deletingCommentId={deletingCommentId}
          newComment={newComment}
          setNewComment={setNewComment}
          editingCommentId={editingCommentId}
          editingCommentContent={editingCommentContent}
          setEditingCommentContent={setEditingCommentContent}
          onAdd={handleAddComment}
          onStartEdit={startCommentEdit}
          onCancelEdit={cancelCommentEdit}
          onSaveEdit={handleUpdateComment}
          onDelete={handleDeleteComment}
        />
      ) : null}
    </div>
  );
};

export default OperationDetailsPage;
